/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  IconButton,
  Portal,
  Snackbar,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { formatCurrency, PartnerInvoiceStatus } from '@omega-energia/utilities';
import { DateTime } from 'luxon';
import { CheckCircle, CheckCircleOutline, Create, DeleteOutline, Payment } from '@material-ui/icons';
import { formatCNPJ, formatCPF, isValidCNPJ } from '@brazilian-utils/brazilian-utils';
import { queryCache, useMutation } from 'react-query';
import { ParceirosTableRowStyles } from '../Parceiros.style';
import { AlterarValorParcelaDialog } from './AlterarValorParcelaDialog';
import { AlterarPercentualComissaoDialog } from './AlterarPercentualComissaoDialog';
import { ExcluirInvoicePaymentDialog } from './ExcluirInvoicePaymentDialog';
import { MarcarPagamentoJaPagoDialog } from './MarcarPagamentoJaPagoDialog';
import ModalConsumerUnits from './ModalConsumerUnits';
import { useAuth } from '../../../../../auth/authProvider';
import partnershipRequest from '../../../../../services/network/partnershipRequest';

const ML_PRODUCT_TYPES = ['DESCONTO_GARANTIDO', 'PREÇO_FIXO', 'LONGO_PRAZO', 'CURTO_PRAZO'];
const ALREADY_PAID_REFERENCE_DATE = '2023-01-01';

export function ParceirosPagamentosTableRow(props) {
  const { paymentsData, isFirstRow, isLastRow, invoiceData, refetchInvoices } = props;
  const { token } = useAuth();
  const useRowStyles = makeStyles(ParceirosTableRowStyles);
  const classes = useRowStyles();
  const [enableEdit, setEnableEdit] = useState(null);
  const [showConsumerUnitsModal, setShowConsumerUnitsModal] = useState(false);
  const [alert, setAlert] = useState({ show: false, msg: '' });

  const isPayment = paymentsData.type === 'payment';
  const isBonus = paymentsData.type === 'bonus';
  const isReferrer = paymentsData.type === 'referrer';
  const isMGM = paymentsData.type === 'mgm_indirect';
  const isML = isPayment && ML_PRODUCT_TYPES.includes(paymentsData?.comissioningLead?.productType);
  const referrerName = paymentsData?.referrerName;

  const customerName = isMGM
    ? paymentsData?.indicatorInfo?.fullName ||
      paymentsData?.indicatorInfo?.businessName ||
      paymentsData?.indicatorInfo?.customerName ||
      '--'
    : isPayment || isReferrer
    ? paymentsData?.comissioningLead?.companyName || paymentsData?.comissioningLead?.customerName
    : isBonus
    ? paymentsData?.bonusName
    : paymentsData?.name;

  const identificationNumber = paymentsData?.comissioningLead?.identificationNumber || '--';
  const installmentType = paymentsData?.installmentType;
  const installmentPercentage = paymentsData?.installmentPercentage;
  const installmentAmount = isPayment
    ? paymentsData?.installmentAmount
    : isBonus
    ? paymentsData?.bonusAmount
    : paymentsData?.amount;
  const comissionPercentage = isPayment ? `${paymentsData?.comissionPercentage * 100} %` : '--';
  const shouldShowComissionPercentage = Number(paymentsData?.comissionPercentage) > 0;
  const comissionPercentageDisplay = shouldShowComissionPercentage ? comissionPercentage : '--';
  const energyUtility = isMGM
    ? paymentsData?.metadata?.energyUtility
    : isBonus
    ? '--'
    : paymentsData?.comissioningLead?.energyUtility || '--';
  const contractValidationDate = paymentsData?.comissioningLead?.contractValidationDate;
  const billingInvoicePaymentDate = paymentsData?.comissioningLead?.billingInvoicePaymentDate;
  const referenceDate = isBonus
    ? paymentsData?.referenceDate
    : isMGM
    ? paymentsData?.cycleDate
    : installmentType === 'CONTRACT_SIGNING'
    ? contractValidationDate
    : billingInvoicePaymentDate;
  const consumerUnitsData = paymentsData?.comissioningLead?.consumerUnitsData || [];
  const consumerUnitsCount = consumerUnitsData.length;
  const source = paymentsData?.source;
  const createdBy = paymentsData?.createdBy;
  const isManualOrigin = isPayment && source === 'MANUAL_BACKSTAGE';
  const originLabel = isManualOrigin ? 'Manual' : 'Sistema';
  const validationState = typeof paymentsData?.isValidated === 'boolean' ? paymentsData.isValidated : null;
  const editableStatuses = [
    PartnerInvoiceStatus.AGUARDANDO_VALIDACAO_INTERNA,
    PartnerInvoiceStatus.AGUARDANDO_VALIDACAO_PARCEIRO,
  ];
  const canUpdatePaymentValues = !!invoiceData?.status && editableStatuses.includes(invoiceData.status);
  const canUpdatePaymentValidation = isPayment && canUpdatePaymentValues;
  const invoiceReferenceDate = invoiceData?.referenceDate
    ? DateTime.fromISO(String(invoiceData.referenceDate)).isValid
      ? DateTime.fromISO(String(invoiceData.referenceDate)).toISODate()
      : String(invoiceData.referenceDate).slice(0, 10)
    : null;
  const isAlreadyPaidInvoice =
    invoiceData?.status === PartnerInvoiceStatus.FLUXO_MANUAL && invoiceReferenceDate === ALREADY_PAID_REFERENCE_DATE;

  const formatDocument = doc => {
    if (!doc) {
      return '--';
    }
    try {
      if (isValidCNPJ(doc)) {
        return formatCNPJ(doc);
      }
      return formatCPF(doc);
    } catch (error) {
      return doc;
    }
  };

  const formatDate = date => {
    if (!date) {
      return '--';
    }
    try {
      return DateTime.fromISO(date).toFormat('dd/MM/yyyy');
    } catch (error) {
      return '--';
    }
  };

  const handleClose = () => {
    setEnableEdit(null);
  };

  const renderValidationStatus = () => {
    if (!isPayment || validationState === null) {
      return null;
    }

    return (
      <Box display="flex" alignItems="center" style={{ gap: '6px', marginTop: '4px' }}>
        <Box
          component="span"
          style={{
            width: '8px',
            height: '8px',
            minWidth: '8px',
            borderRadius: '50%',
            backgroundColor: validationState ? '#2E7D32' : '#B0B7C3',
          }}
        />
        <Typography variant="caption" style={{ color: validationState ? '#2E7D32' : '#6B7280' }}>
          {validationState ? 'Validado' : 'Pendente validação'}
        </Typography>
      </Box>
    );
  };

  const getInstallment = () => {
    if (installmentType === 'CONTRACT_SIGNING' && isPayment && installmentPercentage === 100) {
      return '1/1';
    }
    if (installmentType === 'CONTRACT_SIGNING' && isPayment && installmentPercentage !== 100) {
      return '1/2';
    }
    if (installmentType === 'FIRST_BILL_PAID' && isPayment) {
      return '2/2';
    }
    return '1/1';
  };

  const [togglePaymentValidation, { isLoading: isTogglingValidation }] = useMutation(
    () =>
      partnershipRequest.patch(
        `invoices/partner-invoice-comissioning-lead/${paymentsData?.id}/validation`,
        { isValidated: !validationState },
        { token },
      ),
    {
      onSuccess: () => {
        setAlert({
          show: true,
          msg: validationState ? 'Validação do pagamento removida' : 'Pagamento marcado como validado',
        });
        queryCache.refetchQueries('fetch-payment-validations');
      },
      onError: error => {
        setAlert({ show: true, msg: error?.message || error || 'Erro ao atualizar validação do pagamento' });
      },
    },
  );

  const formatSecondaryLine = () => {
    if (isReferrer) {
      return `Parceiro: ${referrerName}`;
    }
    if (isMGM) {
      const indicatorInfo = paymentsData?.indicatorInfo;
      if (indicatorInfo) {
        return `MGM: ${indicatorInfo.totalConsumption || 0} kWh (${indicatorInfo.leadsCount || 0} leads)`;
      }
      return `MGM: ${paymentsData?.metadata?.customerName || '--'}`;
    }
    if (isPayment) {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography component="span" variant="body2">
            {formatDocument(identificationNumber)}
          </Typography>
          {consumerUnitsCount > 0 && (
            <Typography
              component="span"
              variant="body2"
              onClick={e => {
                e.stopPropagation();
                setShowConsumerUnitsModal(true);
              }}
              style={{
                color: 'rgb(255, 82, 70)',
                cursor: 'pointer',
                fontWeight: 'bold',
                marginLeft: '4px',
              }}
            >
              - {consumerUnitsCount} UC{consumerUnitsCount !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>
      );
    }
    if (isBonus) {
      return `Trigger: ${paymentsData?.triggerValue || 0} | Atingido: ${paymentsData?.achievedValue || 0}`;
    }
    return 'Campanha';
  };
  return (
    <>
      <TableRow
        className={`
          ${classes.paymentTableRowStyle}
          ${isFirstRow ? classes.firstRowStyle : ''}
          ${isLastRow ? classes.lastRowStyle : ''}
        `}
      >
        <TableCell width="25%">
          <Box display="flex" flexDirection="column">
            <Typography variant="body2">{customerName || '--'}</Typography>
            <Typography variant="body2" className={classes.tableCell}>
              {formatSecondaryLine()}
            </Typography>
            <Typography variant="body2" className={classes.tableCell}>
              {`Origem: ${originLabel}`}
              {isManualOrigin && createdBy ? ` | Responsável: ${createdBy}` : ''}
            </Typography>
            {renderValidationStatus()}
          </Box>
        </TableCell>

        <TableCell width="15%">
          <Typography className={classes.tableCell} variant="body2">
            {isReferrer ? 'PGP' : isMGM ? 'MGM' : isBonus ? 'BÔNUS' : isML ? 'ML' : 'GD'}
          </Typography>
        </TableCell>

        <TableCell width="15%">
          <Typography className={classes.tableCell} variant="body2">
            {getInstallment()}
          </Typography>
        </TableCell>

        <TableCell width="15%">
          <Typography className={classes.tableCell} variant="body2">
            {installmentAmount !== undefined && installmentAmount !== null ? formatCurrency(installmentAmount) : '--'}
            {isPayment && (
              <IconButton
                disabled={!canUpdatePaymentValues}
                onClick={() => setEnableEdit('parcela')}
                style={{ color: '#FF5246' }}
              >
                <Create />
              </IconButton>
            )}
          </Typography>
        </TableCell>
        <TableCell width="15%">
          <Typography variant="body2">
            {comissionPercentageDisplay}
            {isPayment && !isML && (
              <IconButton
                disabled={!canUpdatePaymentValues}
                onClick={() => setEnableEdit('comissao')}
                style={{ color: '#FF5246' }}
              >
                <Create />
              </IconButton>
            )}
          </Typography>
        </TableCell>
        <TableCell width="15%">
          <Typography variant="body2">{formatDate(referenceDate)}</Typography>
        </TableCell>
        <TableCell width="15%">
          <Typography variant="body2">{energyUtility}</Typography>
        </TableCell>
        <TableCell width="15%">
          {isPayment && (
            <Box display="flex" alignItems="center" justifyContent="flex-end" style={{ gap: '4px' }}>
              <Tooltip title={isAlreadyPaidInvoice ? 'Item ja esta na invoice de ja pago' : 'Marcar item como ja pago'}>
                <span>
                  <IconButton
                    disabled={isAlreadyPaidInvoice}
                    onClick={() => setEnableEdit('already-paid')}
                    style={{ color: isAlreadyPaidInvoice ? '#B0B7C3' : '#2E7D32' }}
                  >
                    <Payment />
                  </IconButton>
                </span>
              </Tooltip>
              {isTogglingValidation ? (
                <Box display="flex" alignItems="center" justifyContent="center" width="40px">
                  <CircularProgress size={18} />
                </Box>
              ) : (
                <Tooltip title={validationState ? 'Remover validação do pagamento' : 'Marcar item como validado'}>
                  <span>
                    <IconButton
                      disabled={!canUpdatePaymentValidation}
                      onClick={togglePaymentValidation}
                      style={{ color: validationState ? '#2E7D32' : '#8792A2' }}
                    >
                      {validationState ? <CheckCircle /> : <CheckCircleOutline />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <IconButton onClick={() => setEnableEdit('excluir')}>
                <DeleteOutline />
              </IconButton>
            </Box>
          )}
        </TableCell>
      </TableRow>
      <Portal>
        <AlterarValorParcelaDialog
          show={enableEdit === 'parcela'}
          onClose={handleClose}
          data={{ valorParcela: installmentAmount, paymentId: paymentsData?.id }}
          refetchInvoices={refetchInvoices}
        />
        <AlterarPercentualComissaoDialog
          show={enableEdit === 'comissao'}
          onClose={handleClose}
          data={{ percentualComissao: comissionPercentage, paymentId: paymentsData?.id }}
          refetchInvoices={refetchInvoices}
        />
        <ExcluirInvoicePaymentDialog
          show={enableEdit === 'excluir'}
          onClose={handleClose}
          data={{ paymentId: paymentsData?.id }}
          refetchInvoices={refetchInvoices}
        />
        <MarcarPagamentoJaPagoDialog
          show={enableEdit === 'already-paid'}
          onClose={handleClose}
          data={{ paymentId: paymentsData?.id }}
          refetchInvoices={refetchInvoices}
        />
        <ModalConsumerUnits
          isOpen={showConsumerUnitsModal}
          onClose={() => setShowConsumerUnitsModal(false)}
          consumerUnitsData={consumerUnitsData}
          customerName={customerName}
        />
        <Snackbar
          open={alert.show}
          autoHideDuration={4000}
          message={alert.msg}
          onClose={() => setAlert({ show: false, msg: '' })}
        />
      </Portal>
    </>
  );
}

ParceirosPagamentosTableRow.propTypes = {
  paymentsData: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    bonusName: PropTypes.string,
    bonusAmount: PropTypes.number,
    triggerValue: PropTypes.number,
    achievedValue: PropTypes.number,
    referenceDate: PropTypes.string,
    installmentType: PropTypes.string,
    installmentAmount: PropTypes.number,
    comissionPercentage: PropTypes.number,
    isValidated: PropTypes.bool,
    source: PropTypes.string,
    createdBy: PropTypes.string,
    comissioningLead: PropTypes.shape({
      customerName: PropTypes.string,
      identificationNumber: PropTypes.string,
      productType: PropTypes.string,
      energyUtility: PropTypes.string,
      contractValidationDate: PropTypes.string,
      billingInvoicePaymentDate: PropTypes.string,
      consumerUnitsData: PropTypes.arrayOf(
        PropTypes.shape({
          code: PropTypes.string,
          amount: PropTypes.number,
          previewAmount: PropTypes.number,
          referenceConsumption: PropTypes.number,
          publicId: PropTypes.string,
          accountHolder: PropTypes.shape({
            name: PropTypes.string,
            identificationNumber: PropTypes.string,
          }),
        }),
      ),
    }),
  }),
  isFirstRow: PropTypes.bool,
  isLastRow: PropTypes.bool,
  invoiceData: PropTypes.shape({
    status: PropTypes.string,
    referenceDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  }),
  refetchInvoices: PropTypes.func,
};

ParceirosPagamentosTableRow.defaultProps = {
  paymentsData: {},
  isFirstRow: false,
  isLastRow: false,
  invoiceData: null,
  refetchInvoices: null,
};
